import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Typography, Paper, Button, CircularProgress, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { SonosEqFields } from './SonosEqControls';
import {
  fetchAdminEqDefaults,
  applyAdminEqDefaultsToAllUsers,
  type SonosEqDefaultPreset,
} from '../../services/sonosApi';
import { SONOS_BLUE } from './sonosUiStyles';

type EqValues = { bass: number; treble: number; loudness: boolean };

interface Props {
  isAdmin: boolean;
  isDemoUser?: boolean;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

const SonosDefaultEqAdminSection: React.FC<Props> = ({ isAdmin, isDemoUser = false, onNotify }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [speakers, setSpeakers] = useState<SonosEqDefaultPreset[]>([]);
  const [presets, setPresets] = useState<Record<number, EqValues>>({});
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [selectAllMode, setSelectAllMode] = useState(false);
  const [bulkEq, setBulkEq] = useState<EqValues>({ bass: 0, treble: 0, loudness: false });
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const requestCloseApplyConfirm = useOverlayBackButton(
    applyConfirmOpen,
    () => setApplyConfirmOpen(false),
    'sonos-eq-apply-confirm',
  );
  const onNotifyRef = useRef(onNotify);
  const selectAllModeRef = useRef(selectAllMode);
  selectAllModeRef.current = selectAllMode;
  onNotifyRef.current = onNotify;

  const syncBulkToAllPresets = useCallback((eq: EqValues, speakerList: SonosEqDefaultPreset[]) => {
    setPresets((prev) => {
      const next = { ...prev };
      speakerList.forEach((sp) => {
        next[sp.sonos_speaker_id] = { ...eq };
      });
      return next;
    });
  }, []);

  const applyList = useCallback((list: SonosEqDefaultPreset[]) => {
    setSpeakers(list);
    const map: Record<number, EqValues> = {};
    list.forEach((item) => {
      map[item.sonos_speaker_id] = {
        bass: item.bass ?? 0,
        treble: item.treble ?? 0,
        loudness: item.loudness ?? false,
      };
    });
    setPresets(map);
    if (selectAllModeRef.current && list.length) {
      const first = map[list[0].sonos_speaker_id];
      setBulkEq(first);
      setSelectedId(null);
      return;
    }
    setSelectedId((prev) => {
      if (prev && map[prev]) return prev;
      return list[0]?.sonos_speaker_id ?? null;
    });
  }, []);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetchAdminEqDefaults()
      .then((list) => {
        if (cancelled) return;
        applyList(list);
      })
      .catch(() => {
        if (cancelled) return;
        onNotifyRef.current(t('settings.sonosDefaultEq.loadError'), 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, applyList, t]);

  const selectedName = useMemo(
    () => speakers.find((s) => s.sonos_speaker_id === selectedId)?.nombre ?? '',
    [speakers, selectedId],
  );

  const activeEq = selectAllMode
    ? bulkEq
    : (selectedId != null ? presets[selectedId] : null);

  const eqPanelTitle = selectAllMode
    ? t('settings.sonosDefaultEq.allSpeakersTitle')
    : t('musicSystem.eq.title', { name: selectedName });

  const handleSelectAll = () => {
    const base = selectedId != null
      ? presets[selectedId]
      : (speakers[0] ? presets[speakers[0].sonos_speaker_id] : bulkEq);
    const eq = base ?? { bass: 0, treble: 0, loudness: false };
    setBulkEq(eq);
    syncBulkToAllPresets(eq, speakers);
    setSelectAllMode(true);
    setSelectedId(null);
  };

  const handleSelectOne = (id: number) => {
    setSelectAllMode(false);
    setSelectedId(id);
  };

  const updateActiveEq = useCallback((patch: Partial<EqValues>) => {
    if (selectAllMode) {
      setBulkEq((prev) => {
        const next = { ...prev, ...patch };
        setPresets((p) => {
          const updated = { ...p };
          speakers.forEach((sp) => {
            updated[sp.sonos_speaker_id] = { ...next };
          });
          return updated;
        });
        return next;
      });
      return;
    }
    if (selectedId == null) return;
    setPresets((prev) => ({
      ...prev,
      [selectedId]: { ...prev[selectedId], ...patch },
    }));
  }, [selectAllMode, selectedId, speakers]);

  const handleApplyAll = async () => {
    if (!isAdmin || isDemoUser || !speakers.length) return;
    setApplying(true);
    try {
      const payload = speakers.map((sp) => {
        const eq = presets[sp.sonos_speaker_id] ?? { bass: 0, treble: 0, loudness: false };
        return {
          sonos_speaker_id: sp.sonos_speaker_id,
          bass: eq.bass,
          treble: eq.treble,
          loudness: eq.loudness,
        };
      });
      await applyAdminEqDefaultsToAllUsers(payload);
      onNotifyRef.current(t('settings.sonosDefaultEq.applySuccess'), 'success');
      const list = await fetchAdminEqDefaults();
      applyList(list);
    } catch {
      onNotifyRef.current(t('settings.sonosDefaultEq.applyError'), 'error');
    } finally {
      setApplying(false);
    }
  };

  if (!isAdmin) return null;

  return (
    <>
      <Typography sx={{ color: '#2391FF', fontWeight: 'bold', fontSize: '0.85rem', marginBottom: '8px', marginTop: '20px' }}>
        {t('settings.sonosDefaultEq.title')}
      </Typography>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: '120px' }}>
          <CircularProgress sx={{ color: '#2391FF' }} />
        </Box>
      ) : speakers.length === 0 ? (
        <Paper elevation={0} sx={{ backgroundColor: '#2a2a2a', borderRadius: '8px', padding: '16px', marginBottom: '12px', border: '1px solid #444' }}>
          <Typography variant="body2" sx={{ color: '#ccc' }}>
            {t('settings.sonosDefaultEq.noSpeakers')}
          </Typography>
        </Paper>
      ) : (
        <Paper elevation={0} sx={{ backgroundColor: '#2a2a2a', borderRadius: '8px', padding: '16px', marginBottom: '12px', border: '1px solid #444' }}>
          <Typography variant="body2" sx={{ color: '#ccc', lineHeight: 1.6, marginBottom: '12px' }}>
            {t('settings.sonosDefaultEq.description')}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#aaa', fontSize: '0.8rem' }}>
              {t('settings.sonosDefaultEq.speakerLabel')}
            </Typography>
            <Chip
              label={t('settings.sonosDefaultEq.selectAll')}
              size="small"
              clickable
              onClick={handleSelectAll}
              sx={{
                fontWeight: 700,
                fontSize: 11,
                color: selectAllMode ? '#fff' : SONOS_BLUE,
                bgcolor: selectAllMode ? SONOS_BLUE : 'rgba(35,145,255,0.12)',
                border: `1px solid ${SONOS_BLUE}`,
                '&:hover': { bgcolor: selectAllMode ? '#1a7ae0' : 'rgba(35,145,255,0.2)' },
              }}
            />
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
            {speakers.map((sp) => {
              const on = selectAllMode || sp.sonos_speaker_id === selectedId;
              return (
                <Chip
                  key={sp.sonos_speaker_id}
                  label={sp.nombre}
                  size="small"
                  clickable
                  onClick={() => handleSelectOne(sp.sonos_speaker_id)}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11,
                    color: on ? '#fff' : '#ccc',
                    bgcolor: on ? SONOS_BLUE : '#1a1a1a',
                    border: `1px solid ${on ? SONOS_BLUE : '#444'}`,
                    '&:hover': { bgcolor: on ? '#1a7ae0' : '#252525' },
                  }}
                />
              );
            })}
          </Box>

          {activeEq && (
            <Box sx={{ backgroundColor: '#1e1e1e', borderRadius: '8px', border: '1px solid #333', p: 1.5 }}>
              <Typography sx={{ color: '#fff', fontSize: 14, fontWeight: 700, mb: 1.25 }}>
                {eqPanelTitle}
              </Typography>
              <SonosEqFields
                bass={activeEq.bass}
                treble={activeEq.treble}
                loudness={activeEq.loudness}
                onBassChange={(v) => updateActiveEq({ bass: v })}
                onTrebleChange={(v) => updateActiveEq({ treble: v })}
                onLoudnessChange={(v) => updateActiveEq({ loudness: v })}
                disabled={isDemoUser || applying}
              />
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '14px' }}>
            <Button
              variant="contained"
              onClick={() => setApplyConfirmOpen(true)}
              disabled={isDemoUser || applying || !speakers.length}
              sx={{
                backgroundColor: '#2391FF',
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': { backgroundColor: '#1a7ae0' },
                '&.Mui-disabled': { backgroundColor: '#333', color: '#666' },
              }}
            >
              {applying ? t('common.loading') : t('settings.sonosDefaultEq.applyAllUsers')}
            </Button>
          </Box>
        </Paper>
      )}
      <Dialog
        open={applyConfirmOpen}
        onClose={requestCloseApplyConfirm}
        hideBackdrop
        PaperProps={{
          style: {
            backgroundColor: '#333',
            color: 'white',
            borderRadius: '10px',
            minWidth: '300px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#2391FF', fontSize: '1.1rem', fontWeight: 'bold' }}>
          {t('settings.sonosDefaultEq.applyConfirmTitle')}
        </DialogTitle>
        <DialogContent sx={{ paddingTop: '8px' }}>
          <Typography sx={{ color: 'white' }}>
            {t('settings.sonosDefaultEq.applyConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px' }}>
          <Button
            onClick={requestCloseApplyConfirm}
            sx={{
              color: '#2391FF',
              '&:hover': { backgroundColor: 'rgba(35, 145, 255, 0.1)' },
            }}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => {
              setApplyConfirmOpen(false);
              void handleApplyAll();
            }}
            variant="contained"
            sx={{
              backgroundColor: '#2391FF',
              color: 'white',
              fontWeight: 'bold',
              '&:hover': { backgroundColor: '#1a7ae0' },
            }}
          >
            {t('common.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SonosDefaultEqAdminSection;
